import translate from '@allesauto/i18n';
import { ArrowDown16, ArrowUp16 } from '@carbon/icons-react';
import { sbEditable } from '@storyblok/storyblok-editable';
import clsx from 'clsx';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import AccordionItem, {
  AccordionItemProps,
} from './accordion-item/AccordionItem';

export interface AccordionProps {
  heading?: string;
  items: AccordionItemProps[];
  isMultiple?: boolean;
  isNarrow?: boolean;
  className?: string;
  typeTestId?: string;
  fullScreen?: boolean;
  hasShowMore?: boolean;
  isDefaultOpen?: boolean;
  component: string;
  _uid: string;
  _editable?: string;
}

const Accordion: React.FC<AccordionProps> = (props: AccordionProps) => {
  const {
    heading,
    items,
    fullScreen = true,
    isMultiple = false,
    isNarrow = false,
    className: customClassName,
    typeTestId,
    hasShowMore = false,
    isDefaultOpen = true,
  } = props;
  const intl = useIntl();
  const [showMore, setShowMore] = useState<boolean>(true);
  const maxNumberOfAccordionItems = 5;

  //Default open accordion on SSR
  const [isDefaultOpenSSR, setIsDefaultOpenSSR] =
    useState<boolean>(isDefaultOpen);

  const slicedItems = useMemo(
    () =>
      (items &&
        items.slice(
          0,
          hasShowMore && !showMore ? maxNumberOfAccordionItems : items.length
        )) ||
      undefined,
    [hasShowMore, items, showMore]
  );

  const handleShowMore = useCallback(() => setShowMore((prev) => !prev), []);

  const intialItems: Record<string, boolean> = {};
  items?.forEach((item) => {
    intialItems[item._uid] = false;
  });
  const [accordionItems, setAccordionItems] = useState(intialItems);

  const className = {
    accordionHeading: clsx(
      'border-b-1px border-b-grey30 font-headline font-weight-bold leading-7 pb-25px text-grey100 text-headline3 md:leading-8 md:pb-30px',
      !isNarrow && 'lg:pb-40px'
    ),
    iconShowMore: 'inline-block text-purple80 monochrome:text-grey90',
    textShowMore:
      'underline-offset-[6px] inline-block leading-5 ml-[9px] text-p1 text-purple80 underline monochrome:text-grey90',
    wrapperShowMore: 'cursor-pointer mt-[28px] text-center',
  };

  const { accordionHeading, iconShowMore, textShowMore, wrapperShowMore } =
    className;

  const toggleItem = (id: string) => {
    const newItems = { ...accordionItems };
    if (!isMultiple && !newItems[id]) {
      setAccordionItems({ ...intialItems, [id]: true });
    } else {
      newItems[id] = !newItems[id];
      setAccordionItems(newItems);
    }
  };

  useEffect(() => {
    setIsDefaultOpenSSR(false);
    setShowMore(false);
  }, []);

  return (
    <div {...sbEditable(props)} className={customClassName}>
      {heading && (
        <h2 className={accordionHeading} id={`label--${typeTestId}-areaTitle`}>
          {heading}
        </h2>
      )}
      {slicedItems &&
        slicedItems.map((item, key) =>
          item.children ? (
            <AccordionItem
              key={key}
              _uid={item._uid}
              fullScreen={fullScreen}
              title={item.title}
              listItemMarker={item.listItemMarker}
              listItemMarkerColor={item.listItemMarkerColor}
              isOpen={accordionItems[item._uid]}
              onToggle={toggleItem}
              isNarrow={isNarrow}
              context={item.context}
              tabIndex={0}
              listItems={item.listItems}
              link={item.link}
              linkText={item.linkText}
              testId={typeTestId && `accordion--${typeTestId}-title-${key}`}
              isDefaultOpen={isDefaultOpenSSR}
            >
              <div>{item.children as ReactNode}</div>
            </AccordionItem>
          ) : (
            !('children' in item) && (
              <AccordionItem
                key={key}
                _uid={item._uid}
                title={item.title}
                listItemMarker={item.listItemMarker}
                listItemMarkerColor={item.listItemMarkerColor}
                fullScreen={fullScreen}
                isOpen={accordionItems[item._uid]}
                onToggle={toggleItem}
                isNarrow={isNarrow}
                context={item.context}
                tabIndex={0}
                childHtml={item.childHtml}
                listItems={item.listItems}
                link={item.link}
                linkText={item.linkText}
                textComponent={item.textComponent}
                testId={typeTestId && `accordion--${typeTestId}-title-${key}`}
                isDefaultOpen={isDefaultOpenSSR}
              ></AccordionItem>
            )
          )
        )}
      {hasShowMore && (
        <div className={wrapperShowMore} onClick={handleShowMore}>
          {showMore ? (
            <ArrowUp16 className={iconShowMore} />
          ) : (
            <ArrowDown16 className={iconShowMore} />
          )}
          <span className={textShowMore}>
            {translate(
              intl,
              showMore ? 'ui.ACCORDION.SHOW_LESS' : 'ui.ACCORDION.SHOW_MORE'
            )}
          </span>
        </div>
      )}
    </div>
  );
};

export default Accordion;
